/* Filtrous stock badge - v12, 2026-09-03 (back-in-stock send button greyed -
   "blue and grey"). v11 same day (out-of-stock now beats a vetted
   preorder date; blockCart no longer greys the notify button). v10 same day
   (renderer-agnostic /cart/add.js backstop - see addIsBlocked). v9 same day (audit fixes: the FBT/recently-viewed
   "Sold Out" buttons that carry no data-vid, productHref() walking past a
   wrapping <a>, and cards showing a dead control with no stock line).
   v8 same day (every renderer's buy control mirrors the product page - cards,
   quick view, FBT bundle, cart upsell). v7/v6 same day (cards + quick view).
   v5 same day ("backorder should not be addable"). v4 same day (PDP badge on
   the in-app #/p/<sku> route). v3 same day ("it should only be preorder if we
   have a lead time, otherwise it's backorder"). v2 same day ("we should say
   sold out if sold out"). v1 2026-08-27 ("the OSK SKUs are not sold out").

   THE RULE (Adelaide, 2026-09-03) - what may be added to cart:
     preorder WITH a vetted ship date  -> yes
     in stock                          -> yes
     backorder (no confirmed date)     -> NO
     preorder WITHOUT a lead date      -> NO  (no such state renders: every
                                          Preorder label carries a date, and a
                                          dateless one is labelled Backorder)
     out of stock                      -> NO

   The three stock states, told apart by lead time:

     Preorder   orderable AND we have a vetted ship date. Only the hardcoded
                PREORDER / PREORDER_ALT lists in filtrous-v3.js and
                HF_PREORDER / HF_PREORDER_ALT in filtrous-home-families.js
                qualify - someone confirmed those lead times. They render
                "Preorder - ships <date>", stay orderable, and this file leaves
                them alone.
     Backorder  zero on hand, NO confirmed date. Discovered live from Shopify
                (availableForSale && currentlyNotInStock), so nobody has vetted
                a lead time. ~147 of 600 SKUs. NOT orderable - see blockCart().
                Shopify would take the order (these are CONTINUE variants),
                which is exactly why the block is asserted here.
     Sold Out   availableForSale false. Cannot be ordered at all; Shopify
                rejects it server-side too, the variant being DENY.

   The app asserts stock in two places - stockCell() in filtrous-v3.js for the
   sheet + PDP, productRow() in filtrous-home-families.js for search rows - and
   both compute it from p.available alone. The merch renderers do not compute it
   at all. What that got wrong, and what this file fixes:

   1. QUOTE items (OSK-*, EXK-BD-0096-PP) are never stocked to order. Price and
      buy button already switch to "Call for Quote" via isOskQuote(), but the
      stock cell fell through to the on-hand logic and read "Sold Out". They
      must not assert stock at all: the badge reads "Call for Quote" too.
   2. BACKORDER items read "Backorder" - no date, because none is confirmed.
      The amber "pre" class is reused for styling: it is the intermediate
      colour, and reusing it avoids adding CSS and widening the class regex in
      setState(). The /\bpre\b/ early return in apply() still protects the
      hardcoded "Preorder - ships <date>" rows, which arrive already carrying
      that class from their own renderer.
   3. "BACKORDER" IN SEARCH ROWS. productRow() renders its own badge instead of
      calling stockCell(), and its third state is hardcoded:
        hfIsPre(p) ? "Preorder - ships <date>" : p.available ? "In stock"
                                                             : "Backorder"
      So an unavailable part read "Sold Out" on the grid and PDP but "Backorder"
      in search - the most misleading word available, since it promises
      orderable-ships-later when availableForSale is false. Surfaced by
      MBP-21-4425 (Alliance 70% Nitric Acid), whose remaining units were
      committed to a customer, so its policy went CONTINUE -> DENY. In
      productRow() "Backorder" is emitted if and only if p.available is false,
      so the rewrite is unconditional - and it runs LAST, after the BACKORDER
      branch has already claimed the genuinely-orderable rows.
   4. THE PDP BADGE ON THE IN-APP ROUTE. See currentPdpSku().
   5. BACKORDER WAS STILL ADDABLE TO CART. See blockCart().
   6. MERCH CARDS AND THE QUICK-VIEW MODAL IGNORED STOCK ENTIRELY. See
      paintCards() / paintQuickView(). Two traps there, both because the modal
      is mounted on document.body OUTSIDE #fv3-app: the app-scoped
      MutationObserver never saw it open (hence the second observer in boot()),
      and #fv3-app-prefixed CSS never reached it (hence the unscoped rules).

   Runs as a post-pass over what the other renderers produce, rather than inside
   any one of them, so every surface is corrected from a single file.

   COVERAGE (v9): every Add control on the storefront is now gated on stock -
   .fv3-add (grid, search rows, PDP), .pc-add (merch rails), .fqv-add (quick
   view), .fbt-cardadd + the bundle tick-boxes (Frequently bought together /
   Complete the protocol), and .fcu-add (cart drawer upsell). Each keys off
   whatever identifier its renderer exposes: SKU text, data-vid, or the handle
   in the row's product link - hence BY_VID and BY_HANDLE. Where a renderer has
   already worked stock out for itself, its own output is the signal: an
   .fbt-cardadd with NO data-vid and the text "Sold Out" is filtrous-fbt.js
   saying the part is unavailable, and gets the notify treatment on that basis.

   v10 adds a backstop at /cart/add.js so a renderer that is missed, or added
   later, cannot actually sell an unbuyable part. The per-renderer gating stays:
   the backstop is the last line, not the first - a customer should see that a
   part is unavailable, not click and be refused. It fails open by design; see
   addIsBlocked().

   BACK-IN-STOCK CAPTURE (verified 2026-09-03, not changed here beyond colour):
   the navy .fv3-notify-btn reveals .fv3-notify[data-sku]; wireNotify() in
   filtrous-v3.js validates the address, then notifyBackInStock() POSTs
   {email, sku, product} no-cors to BACK_IN_STOCK_URL - the Filtrous Apps
   Script, i.e. the Google Sheet, the same endpoint the newsletter signup uses.
   It only falls back to Shopify's /contact form if that constant is ever
   emptied, and CFG.mock would make it a no-op; neither applies on 1.13.

   Additive asset; remove its loader line in sections/filtrous-v3-app.liquid to
   revert. */
(function () {
  'use strict';

  /* mirrors isOskQuote() in filtrous-v3.js - keep the two in sync */
  function isQuote(sku) {
    var s = String(sku || '').toUpperCase();
    return s.indexOf('OSK') >= 0 || s === 'EXK-BD-0096-PP';
  }

  /* BACKORDER/UNAVAIL are keyed by SKU, but the FBT tick-boxes and card adds
     identify a part only by variant id, and the cart-drawer upsell only by the
     handle in its title link - so the index tap builds both lookups too. */
  var BACKORDER = {}, UNAVAIL = {}, BY_VID = {}, BY_HANDLE = {};

  if (!document.getElementById('fsb-css')) {
    var st = document.createElement('style');
    st.id = 'fsb-css';
    st.textContent =
      '.fv3-stock.quote,.pdp-stock.quote{color:var(--navy,#082F4B);font-weight:400}' +
      '.fv3-stock.quote .dotm{display:none}' +
      /* Backordered parts keep their controls in place but greyed, rather than
         vanishing the way an unavailable row does in filtrous-v3.js. The theme
         styles .fcu-add:disabled / .pc-add:disabled but not .fv3-add.

         NOT scoped under #fv3-app. The quick-view modal is mounted on
         document.body, outside the app root, so an #fv3-app-prefixed rule does
         not reach it - the notify button rendered as a bare underlined link
         until this was unscoped. Every class here is Filtrous-specific, so
         there is nothing else on the page for them to hit. */
      '.fv3-add:disabled,.fv3-add[disabled],.pc-add:disabled,.fqv-add:disabled' +
      '{background:#e3e9ee;color:#8496a3;cursor:default}' +
      '.fv3-qty:disabled,.fv3-step:disabled{background:#f2f5f7;color:#8496a3;cursor:default}' +
      /* matches the PDP notify button exactly - #082F4B navy, white, 700, 8px
         radius - so an unavailable card reads the same as its product page.
         Values lifted from the computed style of .fv3-notify-btn. */
      '.fsb-notify{display:block;width:100%;box-sizing:border-box;background:#082F4B;color:#fff;' +
      'font-weight:700;font-size:13.5px;font-family:inherit;line-height:1.3;border:0;border-radius:8px;' +
      'padding:9px 14px;text-align:center;text-decoration:none;cursor:pointer}' +
      '.fsb-notify:hover{background:#0d3f63;color:#fff}' +
      /* the FBT card add and the cart-drawer upsell add are flex children in a
         row beside a stepper, so a full-width block there would break the
         layout - this variant sits in the row the way the button it replaces
         did (12.5px / 7px radius, matching .fbt-cardadd and .fcu-add). */
      '.fsb-notify.fsb-inline{display:inline-flex;align-items:center;justify-content:center;' +
      'width:auto;flex:1 1 auto;font-size:12.5px;padding:8px 12px;border-radius:7px}' +
      '.fsb-notify.fsb-nowrap{flex:0 0 auto;white-space:nowrap;font-size:12px;padding:8px 11px}' +
      /* mirrors .pc-pre, the note the card renderer already uses for
         "Preorder - ships <date>", so the state reads the same way */
      '.fsb-note{font-size:11.5px;font-weight:600;margin:2px 0 4px}' +
      /* v12 (Adelaide: the back-in-stock control "should be blue and grey").
         The reveal button is already navy; filtrous-v3.js styles the send
         button teal, the same colour as Add to cart, which reads as a buy
         action on a part that cannot be bought. Grey is #5F7181 - already in
         the palette (the .fcu-qv label and the newsletter placeholder), not an
         invented colour. Two classes deep so it wins over v3's single-class
         rule regardless of which <style> is appended last. */
      '.fv3-notify .fv3-notify-send{background:#5F7181;color:#fff}' +
      '.fv3-notify .fv3-notify-send:hover{background:#4c5c69}' +
      '.fv3-notify .fv3-notify-send:disabled{background:#e3e9ee;color:#8496a3;cursor:default}';
    document.head && document.head.appendChild(st);
  }

  /* Read-only tap on the catalog index (same pattern as
     filtrous-recently-viewed.js). Loads AFTER filtrous-live-stock.js so the
     p.backorder flag it sets is already on the payload. */
  /* ---- renderer-agnostic cart backstop -------------------------------------
     Every Add control is gated individually above, but each one had to be
     FOUND first, and twice in one session a renderer turned out to build its
     own button that grepping the minified source had not revealed
     (filtrous-fbt.js's no-data-vid "Sold Out" button; the recently-viewed rail
     reusing .fbt-cardadd). This catches whatever is still missed, and anything
     added later, at the one point every renderer must pass through.

     It is deliberately the LAST line of defence, not the first: the buttons are
     still gated, because a customer should see that a part is unavailable
     rather than click and be refused.

     FAILS OPEN, always. It blocks only when the body parses cleanly AND a
     variant id in it is positively known unbuyable. An unparseable body, an
     unknown id, a Request object (whose body is a stream that reading would
     consume), or an XHR caller all fall straight through. A missed block is a
     wrong badge; a wrongly blocked add is a lost order, so the asymmetry runs
     that way on purpose.

     The FBT "Add all" posts several items at once. If any one is blocked the
     whole batch is refused rather than silently part-ordered - the tick-boxes
     already exclude unbuyable items, so reaching here means something else is
     wrong and the honest move is to refuse the whole thing. */
  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;

  function blockedVid(id) {
    var sku = BY_VID[String(id)];
    return sku ? !!(UNAVAIL[sku] || BACKORDER[sku]) : false;
  }

  function addIsBlocked(init) {
    try {
      var body = init && init.body, ids = [], j, m, v, i;
      if (!body) return false;
      if (typeof body === 'string') {
        if (body.charAt(0) === '{') {
          j = JSON.parse(body);
          if (j.id != null) ids.push(j.id);
          if (j.items && j.items.length) {
            for (i = 0; i < j.items.length; i++) if (j.items[i] && j.items[i].id != null) ids.push(j.items[i].id);
          }
        } else {
          m = body.match(/(?:^|&)id=([^&]+)/);        /* urlencoded id=123&quantity=1 */
          if (m) ids.push(decodeURIComponent(m[1]));
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData && body.get) {
        v = body.get('id');
        if (v != null) ids.push(v);
      } else return false;
      for (i = 0; i < ids.length; i++) if (blockedVid(ids[i])) return true;
      return false;
    } catch (e) { return false; }
  }

  /* shaped like Shopify's own refusal so existing catch/.description handling
     in the renderers reports it rather than throwing */
  function cartRefusal() {
    return new Response(JSON.stringify({
      status: 422, message: 'Cart Error',
      description: 'This item is not available to order right now.'
    }), {
      status: 422, statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  var orig = window.fetch;
  if (orig) {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      /* checked BEFORE orig runs - after it, the item is already in the cart */
      if (CART_ADD.test(String(url)) && typeof input === 'string' && addIsBlocked(arguments[1])) {
        window.console && console.info && console.info('[fsb] blocked an unbuyable add: ' + String(url));
        return Promise.resolve(cartRefusal());
      }
      var p = orig.apply(this, arguments);
      if (String(url).indexOf('catalog-index') === -1) return p;
      return p.then(function (res) {
        if (res && res.ok) {
          res.clone().json().then(function (d) {
            ((d && d.products) || []).forEach(function (x) {
              if (!x || !x.sku) return;
              var k = String(x.sku).toUpperCase();
              if (x.backorder) BACKORDER[k] = 1;
              /* the card pass needs to tell "cannot be ordered" from
                 "orderable but empty", and only the index carries that -
                 nothing in a rail card's markup says so. */
              if (x.available === false) UNAVAIL[k] = 1;
              if (x.vid) BY_VID[String(x.vid)] = k;
              if (x.handle) BY_HANDLE[String(x.handle)] = k;
            });
            paint();
          }).catch(function () {});
        }
        return res;
      });
    };
  }

  function setState(el, cls, text) {
    el.className = el.className.replace(/\b(in|out|pre|quote)\b/g, '').replace(/\s+/g, ' ').trim() + ' ' + cls;
    var dot = el.querySelector('.dotm');
    el.textContent = text;
    if (dot) el.insertBefore(dot, el.firstChild);
    el.setAttribute('data-fsb', '1');
  }

  /* v5 (Adelaide: "backorder should not be addable") - a backordered part must
     not be orderable. Nothing upstream stops it: filtrous-v3.js and
     productRow() only strip or disable the buy controls when p.available is
     false, and a backorder carries p.available === true by construction - that
     is the whole point of the 2026-08-27 split. Shopify will not stop it
     either, because these variants are CONTINUE: /cart/add.js returns 200 and
     the order is taken (verified 2026-09-03). So the block has to be asserted
     here.

     Disabled rather than removed. filtrous-v3.js renders an EMPTY cell for an
     unavailable row, but emptying the cell from a post-pass would fight the
     app's own re-render and shift the layout; greying keeps the control where
     the eye expects it and reads as "not right now" rather than "never". The
     search renderer already disables rather than removes, so this matches it.

     Both the grid and the PDP name their button .fv3-add, and the search row's
     button carries the same class alongside data-hfadd, so one selector covers
     all three. On the PDP the buy row is a SIBLING of the badge, not an
     ancestor, hence the parentElement branch. */
  function disable(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].disabled = true;
      nodes[i].setAttribute('aria-disabled', 'true');
      nodes[i].setAttribute('title', 'On backorder - not available to order');
    }
  }

  function blockCart(el) {
    var scope = (el.closest && el.closest('tr')) || null;
    if (!scope && el.parentElement && el.parentElement.querySelector('.fv3-buyrow')) scope = el.parentElement;
    if (!scope) return;
    /* :not(.fv3-notify-btn) matters - the PDP's "Email me when it's back"
       button carries the .fv3-add class too, and greying that out would take
       away the one action left on an unavailable part. */
    disable(scope.querySelectorAll('.fv3-add:not(.fv3-notify-btn), input.fv3-qty, button.fv3-step, .fv3-buyrow .dec, .fv3-buyrow .inc'));
  }

  /* v6 (Adelaide: "the product cards should match the add to cart options if
     offered. If something is out of stock then the product card should be a
     blue with email me when it's back just like the product page reflects it")

     The merch renderers - filtrous-pdp-merch.js (Complete The Protocol,
     Recommended For You, Products related, Recently viewed),
     filtrous-quick-view.js - build their own product cards with their own Add
     buttons (.pc-add / .fqv-add) and never consult availability, so an
     unavailable part offered an Add button that Shopify would then refuse, and
     a backordered one offered an Add that would silently succeed. Both now
     mirror what the product page shows for the same part:

       unavailable  ->  navy "Email me when it's back", linking to the PDP,
                        where the real notify form lives. Reproducing that form
                        inside a rail card would mean duplicating its wiring;
                        sending the customer one click to the working one is
                        both simpler and where they can read the full detail.
       backorder    ->  Add greyed out, same as the PDP buy row.
       otherwise    ->  left exactly as the merch renderer built it.

     Cards are stamped data-fsbc so the pass is idempotent - filtrous-pdp-merch.js
     re-inserts its rail on every observer pass, and an unstamped rewrite would
     ping-pong with it (see the section-order note in that file's header). A
     rebuilt card arrives unstamped and is simply re-treated. */
  function notifyEl(href, label) {
    var a = document.createElement('a');
    a.className = 'fsb-notify';
    a.setAttribute('href', href || '#');
    a.textContent = label || 'Email me when it’s back';
    return a;
  }

  /* null means "leave whatever the renderer produced alone". Quote parts are
     null on purpose - every renderer already branches on isOskQuote() and
     shows "Call for Quote"; second-guessing that here would fight it. */
  function stateOf(sku) {
    if (!sku || isQuote(sku)) return null;
    if (UNAVAIL[sku]) return 'unavail';
    if (BACKORDER[sku]) return 'backorder';
    return null;
  }

  /* nearest product link at or above el - the FBT card, the upsell row and the
     rail card all wrap their button near an <a href="/products/...">, but at
     different depths, so walk rather than assume one. */
  function productHref(el) {
    /* closest() FIRST. The recently-viewed card is itself an
       <a class="fv3-rv-card" href="/products/..."> wrapping the button, and
       walking up querySelector-ing would skip past it (an ancestor <a> is not
       its own descendant) and return the FIRST link of the whole rail - i.e.
       some other product's URL. */
    var a = el.closest && el.closest('a[href*="/products/"]');
    if (a) return a.getAttribute('href');
    var p = el, n = 0, lk;
    while (p && n < 6) {
      lk = p.querySelector && p.querySelector('a[href*="/products/"]');
      if (lk) return lk.getAttribute('href');
      p = p.parentElement; n++;
    }
    return null;
  }

  function handleSku(href) {
    var m = href && href.match(/\/products\/([^/?#]+)/);
    return m ? BY_HANDLE[m[1]] : null;
  }

  /* The card renderer prints title / sku / price and, for the hardcoded
     preorder list, a .pc-pre note - but nothing at all for backorder or sold
     out. A greyed Add with no reason next to it does not "match the product
     page", which states the stock line above the buy control, so add the same
     line here. Placed after .pc-p to sit where .pc-pre sits. */
  function cardNote(card, text, color) {
    if (card.querySelector('.fsb-note') || card.querySelector('.pc-pre')) return;
    var body = card.querySelector('.pc-body'), price = card.querySelector('.pc-p');
    if (!body) return;
    var d = document.createElement('div');
    d.className = 'fsb-note';
    d.style.color = color;
    d.textContent = text;
    if (price && price.parentNode === body) body.insertBefore(d, price.nextSibling);
    else body.appendChild(d);
  }

  function paintCards() {
    var cards = document.querySelectorAll('#fv3-app .fv3-pcard');
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.getAttribute('data-fsbc')) continue;
      var b = c.querySelector('.pc-sku b'), add = c.querySelector('.pc-add');
      if (!b || !add) continue;
      var sku = b.textContent.trim().toUpperCase();
      /* quote parts are already handled by the merch renderer's own
         isOskQuote() branch - do not second-guess it */
      if (isQuote(sku)) { c.setAttribute('data-fsbc', '1'); continue; }
      if (UNAVAIL[sku]) {
        var lk = c.querySelector('a.pc-link');
        cardNote(c, 'Sold Out', '#A2492F');
        add.parentNode.replaceChild(notifyEl(lk && lk.getAttribute('href')), add);
        c.setAttribute('data-fsbc', '1');
      } else if (BACKORDER[sku]) {
        cardNote(c, 'Backorder', '#b26a00');
        disable(c.querySelectorAll('.pc-add'));
        c.setAttribute('data-fsbc', '1');
      }
    }
  }

  /* The modal carries its own badge, span.fqv-stock, which the quick-view
     renderer fills from p.available alone - so it read "In stock" for a part
     the same modal was refusing to sell. Colours match what the PDP computes:
     #A2492F for out, #b26a00 for the amber intermediate. */
  function fqvBadge(body, text, color) {
    var b = body.querySelector('.fqv-stock');
    if (!b) return;
    var dot = b.querySelector('.dotm');
    b.textContent = text;
    if (dot) b.insertBefore(dot, b.firstChild);
    b.style.color = color;
  }

  /* filtrous-fbt.js: per-card "Add to cart" buttons keyed by data-vid, plus the
     bundle tick-boxes feeding "Add all N to Cart". Neither consults stock.
     Unbuyable tick-boxes are unchecked AND locked, then one change event is
     dispatched so the FBT's own sync() recomputes the button's count and
     disables it at zero - cheaper and safer than reimplementing that sum. */
  function paintFbt() {
    var i, n, st, a;
    /* NOT [data-vid]. filtrous-fbt.js already knows about unavailable parts:
       it omits data-vid and renders a disabled button reading "Sold Out"
       (2 of 20 on a typical PDP). Selecting only [data-vid] skipped exactly
       the parts that most need the notify treatment, leaving a dead grey
       "Sold Out" button where the product page offers to email the customer.
       With no vid to look up, the renderer's own label is the signal.
       These buttons also power the Recently viewed rail (.fv3-rv), which
       reuses .fbt-cardadd rather than .fv3-pcard. */
    var btns = document.querySelectorAll('.fbt-cardadd');
    for (i = 0; i < btns.length; i++) {
      n = btns[i];
      if (n.getAttribute('data-fsbc')) continue;
      var vid = n.getAttribute('data-vid');
      st = vid ? stateOf(BY_VID[vid])
               : (/sold\s*out/i.test(n.textContent) ? 'unavail' : null);
      n.setAttribute('data-fsbc', '1');
      if (st === 'unavail') {
        a = notifyEl(productHref(n));
        a.className = a.className + ' fsb-inline';
        n.parentNode.replaceChild(a, n);
      } else if (st === 'backorder') disable([n]);
    }
    var cbs = document.querySelectorAll('.fv3-fbt input[type="checkbox"][data-vid]'), changed = null;
    for (i = 0; i < cbs.length; i++) {
      n = cbs[i];
      if (n.getAttribute('data-fsbc')) continue;
      st = stateOf(BY_VID[n.getAttribute('data-vid')]);
      n.setAttribute('data-fsbc', '1');
      if (!st) continue;
      if (n.checked) { n.checked = false; changed = n; }
      n.disabled = true;
      n.setAttribute('title', st === 'unavail' ? 'Out of stock' : 'On backorder - not available to order');
    }
    if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* filtrous-cart-upsell.js: .fcu-add in the cart drawer. Its rows carry no
     sku or vid attribute, only the title link, so the handle is the key. */
  function paintCartUpsell() {
    var adds = document.querySelectorAll('.fcu-add');
    for (var i = 0; i < adds.length; i++) {
      var n = adds[i];
      if (n.getAttribute('data-fsbc')) continue;
      var href = productHref(n), st = stateOf(handleSku(href));
      n.setAttribute('data-fsbc', '1');
      if (st === 'unavail') {
        /* "Notify me" here, not the full sentence: .fcu-row is a compact
           image + text + button strip and the long label collapsed to 57px
           under flex, wrapping into the thumbnail. The PDP uses the same
           short label on its secondary notify control. */
        var a = notifyEl(href, 'Notify me');
        a.className = a.className + ' fsb-inline fsb-nowrap';
        n.parentNode.replaceChild(a, n);
      } else if (st === 'backorder') disable([n]);
    }
  }

  function paintQuickView() {
    var body = document.querySelector('.fqv-body');
    if (!body || body.getAttribute('data-fsbc')) return;
    var skuEl = body.querySelector('.fqv-sku'), add = body.querySelector('.fqv-add');
    if (!skuEl || !add) return;
    var m = skuEl.textContent.match(/SKU:\s*([A-Za-z0-9][A-Za-z0-9-]*)/);
    if (!m) return;
    var sku = m[1].toUpperCase();
    if (isQuote(sku)) { body.setAttribute('data-fsbc', '1'); return; }
    if (UNAVAIL[sku]) {
      var lk = body.querySelector('a[href^="/products/"]');
      add.parentNode.replaceChild(notifyEl(lk && lk.getAttribute('href')), add);
      disable(body.querySelectorAll('.fqv-step button'));
      fqvBadge(body, 'Sold Out', '#A2492F');
      body.setAttribute('data-fsbc', '1');
    } else if (BACKORDER[sku]) {
      disable(body.querySelectorAll('.fqv-add, .fqv-step button'));
      fqvBadge(body, 'Backorder', '#b26a00');
      body.setAttribute('data-fsbc', '1');
    }
  }

  function apply(el, sku) {
    if (!el || !sku || el.getAttribute('data-fsb')) return;
    if (isQuote(sku)) { setState(el, 'quote', 'Call for Quote'); return; }
    /* OUT OF STOCK BEATS A VETTED DATE, and is therefore tested before the
       preorder guard below. A SKU can sit on the hardcoded PREORDER list AND
       be availableForSale:false: none are today (checked across all 589 index
       rows on 2026-09-03), but those lists are hand-maintained while the live
       flag is not, so the day one of the 89 preorder parts is discontinued its
       ship date would otherwise keep a dead part looking orderable. The v10
       cart backstop would still refuse the order - this keeps the badge from
       lying about it in the first place. */
    if (UNAVAIL[sku]) { setState(el, 'out', 'Sold Out'); blockCart(el); return; }
    /* otherwise leave the hardcoded "Preorder - ships <date>" rows alone -
       those have a vetted lead time and stay orderable */
    if (/\bpre\b/.test(el.className)) return;
    if (BACKORDER[sku]) { setState(el, 'pre', 'Backorder'); blockCart(el); return; }
    if (el.textContent.trim().toLowerCase() === 'backorder') setState(el, 'out', 'Sold Out');
  }

  /* Which part the PDP badge belongs to. FV3_CONFIG.pdpSku is only stamped on
     the Liquid product page (/products/<handle>); on the in-app route
     #/p/<sku> it is absent, so before v4 paint() skipped the PDP badge there
     entirely and a backordered part kept whatever stockCell() had rendered
     ("In stock"). The route is read FIRST because when both are present it is
     the more current of the two - the app can navigate from one part to
     another without a page load, leaving FV3_CONFIG on the part that was
     server-rendered.

     No stale-badge guard is needed alongside this: the app builds a FRESH
     .pdp-stock element on every in-app navigation (verified 2026-09-03 - a
     probe attribute set on the old node does not survive a #/p/ change), so
     the data-fsb stamp never carries from one part to the next, and blockCart()
     re-runs against the newly rendered buy row. */
  function currentPdpSku() {
    var m = (location.hash || '').match(/^#\/p\/([^/?#]+)/);
    if (m) { try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; } }
    return (window.FV3_CONFIG && window.FV3_CONFIG.pdpSku) || null;
  }

  function paint() {
    var cells = document.querySelectorAll('#fv3-app .fv3-stock');
    for (var i = 0; i < cells.length; i++) {
      var row = cells[i].closest ? cells[i].closest('tr') : null;
      var part = row && row.querySelector('.fv3-part');
      if (part) apply(cells[i], part.textContent.trim().toUpperCase());
    }
    var pdp = document.querySelector('#fv3-app .pdp-stock');
    var sku = currentPdpSku();
    if (pdp && sku) apply(pdp, String(sku).toUpperCase());
    paintCards();
    paintFbt();
    paintDetached();
  }

  /* the quick-view modal and the cart drawer both mount on document.body,
     outside #fv3-app, so these two are driven by their own observer as well */
  function paintDetached() {
    paintQuickView();
    paintCartUpsell();
  }

  function boot() {
    paint();
    new MutationObserver(paint).observe(document.getElementById('fv3-app') || document.body, { childList: true, subtree: true });
    /* The quick-view modal is appended to document.body, OUTSIDE #fv3-app, so
       the observer above never sees it open - its parent chain is
       body > div.on > div > .fqv-grid > .fqv-body (verified 2026-09-03).
       Without this second observer paintQuickView() ran only when something
       else happened to mutate #fv3-app, which in practice meant never, and the
       modal kept a live Add button for an unavailable part. Scoped to
       paintQuickView alone rather than the full paint, and it bails on one
       querySelector when no modal is open or it is already stamped. */
    new MutationObserver(paintDetached).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
